import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  portalWiList, portalWiDetail, portalWiRead, portalWiReworkComplete, portalWiPhotoUrl, portalWiDocumentUrl,
  apiErrText, countdown, wiMoney, wiRate,
  WI_STATE, WI_PRICE_CONFIRMATION_STATES,
  type PortalWiRow, type PortalWiDetail, type PortalWiInstruction,
} from '../lib/api';
import PortalQuoteForm, { PhotoPicker } from './PortalQuoteForm';
import PortalDayworkForm from './PortalDayworkForm';
import PortalPriceConfirmCard from './PortalPriceConfirmCard';
import { Skeleton } from './ui/Skeleton';
import { toast } from './ui/Toast';

/**
 * 工程指令 / work instructions, the contractor's half (Layer 2 PR9 · Eric 9/18).
 *
 * §8 risk 4 is the design brief: the portal is NOT yet the contractual notice channel, so nothing on this
 * screen may look like an enforcement. What it can do — and the only thing that actually closes the
 * post-signing leak — is make the signed 指令单 faster to get than to skip. Hence:
 *   · one card per order, with the clock and the contract's own sentence on it, so 「我没收到」 stops being
 *     a defence and 「我不知道要 7 天内报」 stops being true;
 *   · every state 乙方 may see is a card (WI_LABOR_VISIBLE), including 待签字（未生效）— which renders the
 *     9.1.1 + 4.1.2 clause VERBATIM, because an order nobody has signed is exactly the order a contractor
 *     must not start work on;
 *   · 送达 is recorded the moment a detail opens (G1 read receipt), once, and never re-stamped.
 *
 * Money-free except for THEIR OWN declared rates and OUR audited result: view.ts's labor allow-list is the
 * guarantee, and nothing here fetches anything else.
 */

const KIND_LABEL: Record<string, { label: string; pill: string }> = {
  variation: { label: '变更 / Variation', pill: 'pill-stage' },
  daywork: { label: '零星（计日工）/ Daywork', pill: 'pill-muted' },
};
const ORIGIN_LABEL: Record<string, string> = {
  client: '甲方 / Client', site: '现场 / Site', design: '设计 / Design',
  labor: '劳务 / Contractor', wtl: 'WTL',
};
const SEAT_LABEL: Record<string, string> = { pm: '项目经理 / PM', engineer: '专业工程师 / Engineer', cost: '成本负责人 / Cost' };

function stamp(iso: string | null | undefined): string { return iso ? String(iso).slice(0, 16).replace('T', ' ') : ''; }
function stateOf(wi: PortalWiInstruction) { return WI_STATE[wi.state] ?? { label: wi.state, pill: 'pill-muted' }; }
/** A 变更 order still owes a quote; a 零星 one never does (its bar is the 48-hour daily record). */
function quoteOwed(row: PortalWiRow): boolean {
  return row.instruction.kind === 'variation' && (row.instruction.state === 'served' || row.instruction.state === 'quoted');
}

export default function PortalInstructionsSection({ eid }: { eid: string }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, isLoading } = useQuery<{ items: PortalWiRow[] }>({
    queryKey: ['portal-work-instructions', eid],
    queryFn: () => portalWiList(eid),
    refetchInterval: 60_000,   // matches the nav badge, so the card and the badge cannot disagree
  });

  if (isLoading) return <div className="card"><Skeleton lines={4} /></div>;
  const items = data?.items ?? [];

  if (openId) return <InstructionDetail eid={eid} wiid={openId} onBack={() => setOpenId(null)} />;

  const live = items.filter((r) => r.instruction.state !== 'void' && r.instruction.state !== 'confirmed');
  const done = items.filter((r) => r.instruction.state === 'void' || r.instruction.state === 'confirmed');

  return (
    <div>
      <div className="row-wrap" style={{ gap: 8, alignItems: 'baseline', margin: '10px 0 4px' }}>
        <h3 style={{ margin: 0 }}>工程指令 / Work instructions</h3>
        <span className="muted small">共 {items.length} 条 / {items.length} order(s)</span>
      </div>
      <div className="muted small" style={{ marginBottom: 8 }}>
        变更与零星工程均以书面指令为准；未经书面指令施工的，发包人不予计量、不予支付。提醒目前只在本页和顶部
        「待办」徽标显示，请定期查看。/ Variations and daywork are governed by the written order; reminders live
        on this page and the 待办 badge for now.
      </div>

      {items.length === 0 && (
        <div className="card muted small">暂无工程指令。WTL 下发后会出现在这里。/ No work instructions yet — they appear here once WTL issues one.</div>
      )}

      {live.map((r) => <InstructionCard key={r.instruction.id} eid={eid} row={r} onOpen={() => setOpenId(r.instruction.id)} />)}
      {done.length > 0 && (
        <>
          <div className="muted small" style={{ margin: '12px 0 4px' }}>已完成 / Closed（{done.length}）</div>
          {done.map((r) => <InstructionCard key={r.instruction.id} eid={eid} row={r} onOpen={() => setOpenId(r.instruction.id)} />)}
        </>
      )}
    </div>
  );
}

/** One order, as a phone-sized card: what it is, where it stands, what clock is running, what to do next. */
function InstructionCard({ eid, row, onOpen }: { eid: string; row: PortalWiRow; onOpen: () => void }) {
  const wi = row.instruction;
  const st = stateOf(wi);
  const kind = KIND_LABEL[wi.kind] ?? { label: wi.kind, pill: 'pill-muted' };
  // 零星 orders carry no quote_due_at — the card must NOT draw a 7-day countdown on them (PR4a wiClocks).
  const showClock = row.hours_left != null && !!row.quote_due_at;
  const cd = countdown(row.quote_due_at);
  const unread = wi.state === 'served' && !wi.read_at;

  return (
    <div className="card" style={{ marginBottom: 8, borderLeft: `3px solid ${row.forfeited ? 'var(--bad)' : unread ? 'var(--accent)' : 'var(--rule)'}` }}>
      <div className="row-wrap" style={{ gap: 6, alignItems: 'baseline' }}>
        <span style={{ flex: 1, minWidth: 140, wordBreak: 'break-word' }}>
          {wi.doc_no && <span className="pill small pill-muted" style={{ marginRight: 6 }}>{wi.doc_no}</span>}
          <strong>{wi.title}</strong>
        </span>
        <span className={`pill small ${kind.pill}`} style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{kind.label}</span>
        <span className={`pill small ${st.pill}`} style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{st.label}</span>
      </div>

      {/* 待签字（未生效）carries 9.1.1 + 4.1.2 — rendered VERBATIM, as a quotation, never paraphrased. */}
      {wi.state === 'pending_signatures' && st.note && (
        <blockquote className="small" style={{ margin: '6px 0 0', padding: '6px 10px', borderLeft: '3px solid var(--warn, #9a6b00)', background: 'var(--warn-soft)', color: 'var(--accent)' }}>
          {st.note}
        </blockquote>
      )}

      <div className="row-wrap" style={{ gap: 8, alignItems: 'baseline', marginTop: 4 }}>
        <span className="muted small">发生日期 / occurred {wi.occurred_on}</span>
        {wi.served_at && <span className="muted small">送达 / served {stamp(wi.served_at)}</span>}
        {unread && <span className="pill small pill-warn">未读 / Unread</span>}
        <span className="muted small">签字 / signatures {row.signatures_signed}/{row.signatures_required}</span>
        {wi.origin && <span className="muted small">来源 / origin {ORIGIN_LABEL[wi.origin] ?? wi.origin}</span>}
      </div>

      {(wi.scope_md || wi.location_md) && (
        <div className="muted small" style={{ marginTop: 2, wordBreak: 'break-word' }}>
          {wi.location_md && <>位置 / where：{wi.location_md}　</>}
          {wi.scope_md && <>范围 / scope：{wi.scope_md}</>}
        </div>
      )}

      {/* The countdown pill: red under 48h, 逾期失权 once the right is gone, nothing at all on 零星. */}
      <div className="row-wrap" style={{ gap: 6, alignItems: 'center', marginTop: 6 }}>
        {row.forfeited && !row.late_service && <span className="pill small pill-bad">⛔ 逾期失权 / Time-barred</span>}
        {row.forfeited && row.late_service && <span className="pill small pill-warn">逾期送达，报价通道保留 / Served late — still open</span>}
        {showClock && <span className={`pill small ${cd.expired || cd.urgent ? 'pill-bad' : 'pill-warn'}`} title="报价期限 / quote window">报价 {cd.text}</span>}
        {wi.state === 'approved' && <span className="pill small pill-stage">待认价 / Awaiting your confirmation</span>}
      </div>

      {/* The contract's own words for this state, verbatim, in a muted quote block. */}
      {row.contract_text_zh && wi.state !== 'pending_signatures' && (
        <blockquote className="muted small" style={{ margin: '6px 0 0', padding: '6px 10px', borderLeft: '3px solid var(--rule)' }}>
          {row.contract_text_zh}
        </blockquote>
      )}

      <div className="row-wrap" style={{ gap: 6, alignItems: 'center', marginTop: 8 }}>
        <button className="btn-primary" onClick={onOpen}>查看详情 / Open</button>
        {quoteOwed(row) && !(row.forfeited && !row.late_service) && (
          <button onClick={onOpen}>💰 报价 / Quote</button>
        )}
        {wi.kind === 'daywork' && wi.state !== 'draft' && wi.state !== 'pending_signatures' && (
          <button onClick={onOpen}>📝 填写日报 / Daily record</button>
        )}
        {wi.state === 'approved' && <button className="btn-good" onClick={onOpen}>✓ 确认认价 / Confirm price</button>}
        <a className="small" href={portalWiDocumentUrl(eid, wi.id, 'instruction')}>⬇ 指令单 Word</a>
      </div>
    </div>
  );
}

/** The full order: scope, signatures, our arithmetic, and whichever form its kind and state call for. */
function InstructionDetail({ eid, wiid, onBack }: { eid: string; wiid: string; onBack: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<PortalWiDetail>({
    queryKey: ['portal-work-instruction', eid, wiid],
    queryFn: () => portalWiDetail(eid, wiid),
  });
  const readFired = useRef<string | null>(null);

  /**
   * G1 查阅回执 — ONCE, when the detail first opens, and never again for this order in this session. The
   * server is idempotent anyway (a second call answers the same read_at), but firing on every refetch
   * would write a message row per poll. A failure is swallowed: a read receipt that could not be recorded
   * must not stop a contractor reading the order.
   */
  useEffect(() => {
    if (!data || readFired.current === wiid) return;
    readFired.current = wiid;
    void portalWiRead(eid, wiid)
      .then((res) => {
        if (!res.recorded) return;
        void qc.invalidateQueries({ queryKey: ['portal-work-instructions', eid] });
        void qc.invalidateQueries({ queryKey: ['portal-work-instruction', eid, wiid] });
        void qc.invalidateQueries({ queryKey: ['portal-todo-count'] });
        void qc.invalidateQueries({ queryKey: ['portal-messages', eid] });
      })
      .catch(() => { /* best-effort — never blocks reading the order */ });
  }, [data, eid, wiid, qc]);

  function applyDetail(next: PortalWiDetail) {
    qc.setQueryData<PortalWiDetail>(['portal-work-instruction', eid, wiid], next);
    void qc.invalidateQueries({ queryKey: ['portal-work-instructions', eid] });
    void qc.invalidateQueries({ queryKey: ['portal-todo-count'] });
  }
  function refetchAll() {
    void qc.invalidateQueries({ queryKey: ['portal-work-instruction', eid, wiid] });
    void qc.invalidateQueries({ queryKey: ['portal-work-instructions', eid] });
    void qc.invalidateQueries({ queryKey: ['portal-todo-count'] });
  }

  if (isLoading) return <div className="card"><Skeleton lines={5} /></div>;
  if (!data) return <div className="card">未找到该工程指令。/ Work instruction not found.<div style={{ marginTop: 6 }}><button onClick={onBack}>← 返回 / Back</button></div></div>;

  const wi = data.instruction;
  const st = stateOf(wi);
  const kind = KIND_LABEL[wi.kind] ?? { label: wi.kind, pill: 'pill-muted' };
  const evidence = data.photos.filter((p) => p.kind !== 'daywork');
  const showQuote = wi.kind === 'variation' && (wi.state === 'served' || wi.state === 'quoted');
  const showDaywork = wi.kind === 'daywork' && wi.state !== 'draft' && wi.state !== 'pending_signatures' && wi.state !== 'void';
  const showConfirm = WI_PRICE_CONFIRMATION_STATES.includes(wi.state) || !!data.price_confirmation;
  const showRework = wi.rework_required && !wi.rework_confirmed_at;

  return (
    <div>
      <div className="row" style={{ marginBottom: 8 }}>
        <button onClick={onBack}>← 返回指令列表 / Back to the list</button>
      </div>

      <div className="card">
        <div className="row-wrap" style={{ gap: 6, alignItems: 'baseline' }}>
          <span style={{ flex: 1, minWidth: 140, wordBreak: 'break-word' }}>
            {wi.doc_no && <span className="pill small pill-muted" style={{ marginRight: 6 }}>{wi.doc_no}</span>}
            <strong>{wi.title}</strong>
          </span>
          <span className={`pill small ${kind.pill}`} style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{kind.label}</span>
          <span className={`pill small ${st.pill}`} style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{st.label}</span>
        </div>

        {wi.state === 'pending_signatures' && st.note && (
          <blockquote className="small" style={{ margin: '6px 0 0', padding: '6px 10px', borderLeft: '3px solid var(--warn, #9a6b00)', background: 'var(--warn-soft)', color: 'var(--accent)' }}>
            {st.note}
          </blockquote>
        )}
        {data.contract_text_zh && wi.state !== 'pending_signatures' && (
          <blockquote className="muted small" style={{ margin: '6px 0 0', padding: '6px 10px', borderLeft: '3px solid var(--rule)' }}>
            {data.contract_text_zh}
          </blockquote>
        )}

        <div className="muted small" style={{ marginTop: 6 }}>
          <div>发生日期 / occurred：{wi.occurred_on}{wi.origin ? ` · 来源 / origin：${ORIGIN_LABEL[wi.origin] ?? wi.origin}` : ''}</div>
          {wi.issued_at && <div>下发 / issued：{stamp(wi.issued_at)}{wi.issued_by_name ? ` · ${wi.issued_by_name}` : ''}</div>}
          {wi.effective_at && <div>生效 / effective：{stamp(wi.effective_at)}</div>}
          {wi.served_at && <div>送达 / served：{stamp(wi.served_at)}</div>}
          {wi.read_at && <div>我方查阅 / read by us：{stamp(wi.read_at)}</div>}
          {wi.location_md && <div>位置 / where：{wi.location_md}</div>}
          {wi.stage_md && <div>阶段 / stage：{wi.stage_md}</div>}
          {wi.drawing_ref && <div>图纸变更号 / drawing ref：{wi.drawing_ref}</div>}
          {wi.daywork_category && <div>零星类别 / daywork category：{wi.daywork_category}</div>}
          {wi.schedule_impact_days != null && <div>工期影响 / schedule impact：{wi.schedule_impact_days} 天</div>}
        </div>
        {wi.reason_md && <div className="small" style={{ marginTop: 6 }}><strong>事由 / reason：</strong>{wi.reason_md}</div>}
        {wi.scope_md && <div className="small" style={{ marginTop: 2 }}><strong>范围 / scope：</strong>{wi.scope_md}</div>}

        <div style={{ marginTop: 8 }}>
          <a className="small" href={portalWiDocumentUrl(eid, wi.id, 'instruction')}>⬇ 指令单 Word / Work instruction (.docx)</a>
        </div>
      </div>

      {/* ── 9.1.1 三方签字 ──────────────────────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginTop: 8 }}>
        <div className="small"><strong>甲方签字 / WTL signatures（{data.signatures_signed}/{data.signatures_required}）</strong></div>
        {data.signatures.map((s) => (
          <div key={s.seat} className="row-wrap" style={{ gap: 8, alignItems: 'baseline', padding: '3px 0', borderTop: '1px solid var(--rule-hair)' }}>
            <span className="small" style={{ flex: 1, minWidth: 120 }}>{SEAT_LABEL[s.seat] ?? s.seat}</span>
            {s.signed_at
              ? <span className="small">{s.signed_by_name ?? '—'}{s.on_behalf_of ? `（代 ${s.on_behalf_of} 签）` : ''} · {stamp(s.signed_at)}</span>
              : <span className="pill small pill-warn">待签字 / not signed</span>}
          </div>
        ))}
      </div>

      {/* ── 明细 / scope lines ──────────────────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginTop: 8 }}>
        <div className="small"><strong>工程明细 / Scope lines</strong></div>
        {data.lines.length === 0 && <div className="muted small">本指令无明细行。/ No itemised lines on this order.</div>}
        {data.lines.map((l, i) => (
          <div key={l.id} style={{ padding: '6px 0', borderTop: '1px solid var(--rule)' }}>
            <div className="small"><strong>{i + 1}. {l.item_label}</strong></div>
            {l.spec_md && <div className="muted small">{l.spec_md}</div>}
            <div className="muted small">
              {l.qty ?? '—'} {l.unit ?? ''}
              {l.measurement_calc_md && <> · 计算式 / calc：{l.measurement_calc_md}</>}
            </div>
            {(l.declared_unit_rate_rmb != null || l.audited_unit_rate_rmb != null) && (
              <div className="muted small">
                申报 / declared {wiRate(l.declared_unit_rate_rmb)}
                {l.audited_unit_rate_rmb != null && ['approved', 'confirmed', 'disputed'].includes(data.instruction.state) && <> → 审核 / audited <strong>{wiRate(l.audited_unit_rate_rmb)}</strong> = {wiMoney(l.audited_amount_rmb)}</>}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── 影像资料 ────────────────────────────────────────────────────────────────────────────── */}
      {evidence.length > 0 && (
        <div className="card" style={{ marginTop: 8 }}>
          <div className="small"><strong>影像资料 / Photos（{evidence.length}）</strong></div>
          <div className="row-wrap" style={{ gap: 6, marginTop: 4 }}>
            {evidence.map((p) => (
              <a key={p.id} href={portalWiPhotoUrl(eid, wi.id, p.id)} target="_blank" rel="noreferrer" className="small">
                🖼 {p.caption ?? p.kind}{p.taken_on ? ` · ${p.taken_on}` : ''}
              </a>
            ))}
          </div>
        </div>
      )}

      {showQuote && <PortalQuoteForm eid={eid} detail={data} onUpdated={applyDetail} />}
      {showDaywork && <PortalDayworkForm eid={eid} detail={data} onUpdated={refetchAll} />}
      {showConfirm && <PortalPriceConfirmCard eid={eid} detail={data} onRefetch={refetchAll} />}
      {showRework && <ReworkCompleteCard eid={eid} detail={data} onUpdated={applyDetail} />}
    </div>
  );
}

/**
 * 9.1.4 返工完成 — 乙方 files the evidence, which starts WTL's own 3-工作日 confirmation clock (G3). Small
 * enough to live here rather than in a fifth file, and it shares the quote form's photo picker verbatim.
 */
function ReworkCompleteCard({ eid, detail, onUpdated }: { eid: string; detail: PortalWiDetail; onUpdated: (d: PortalWiDetail) => void }) {
  const wi = detail.instruction;
  const [picked, setPicked] = useState<File[]>([]);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!picked.length) return;
    setBusy(true); setErr(null);
    try {
      const updated = await portalWiReworkComplete(eid, wi.id, picked, notes.trim() || undefined);
      onUpdated(updated);
      setPicked([]); setNotes('');
      toast('返工完成已提交，等待 WTL 确认。/ Filed — WTL will confirm it.', { kind: 'success' });
    } catch (e) {
      setErr(apiErrText(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ marginTop: 10, borderLeft: '3px solid var(--bad)' }}>
      <div className="small"><strong>返工完成 / Rework complete</strong></div>
      <div className="muted small" style={{ marginTop: 2 }}>
        本指令要求返工。完成后请附整改后的现场照片提交，WTL 将在 3 个工作日内确认
        {wi.rework_confirm_due_at ? `（确认期限 ${stamp(wi.rework_confirm_due_at)}）` : ''}。
        / This order requires rework — file the after photos and WTL confirms within 3 working days.
      </div>
      <PhotoPicker picked={picked} onPicked={setPicked} disabled={busy} idPrefix={`rw-${wi.id}`} />
      <input
        className="grow" style={{ width: '100%', marginTop: 6 }} value={notes} disabled={busy}
        onChange={(e) => setNotes(e.target.value)} placeholder="整改说明（选填）/ what was fixed (optional)"
      />
      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>{err}</div>}
      <div className="row-wrap" style={{ gap: 6, alignItems: 'center', marginTop: 8 }}>
        <button className="btn-good" disabled={busy || !picked.length} onClick={() => void submit()}>
          {busy ? '提交中… / submitting…' : '✓ 提交返工完成 / File rework complete'}
        </button>
        {!picked.length && <span className="muted small">请先附整改后照片。/ Attach an after photo first.</span>}
      </div>
    </div>
  );
}
